import AnimationFadeIn from "@components/AnimationFadeIn";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import ButtonBackPage from "@components/ButtonBackPage";
import HeroCst from "@components/HeroCst";
import Table from "@components/Table";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { useFetchDataDetail, useFetchDetail } from "@hooks/useFetchDetail";
import DownloadIcon from "@mui/icons-material/Download";
import { Box, Container, useTheme } from "@mui/material";
import { downloadBlobFile, onErrorMessage, ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { CollectCampaignDto } from "../../types";
import { ActionsType } from "src/interfaces/Common";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import { MRT_RowSelectionState } from "material-react-table";
import { MethodType } from "src/services/type";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import CustomTopToolbar from "@components/Table/components/CustomTopToolbar";
import { buildDateStr } from "@utils/utilsDate";
import FormLoadingStateCst from "@components/FormLoadingStateCst";

const MonitoringExternalTraining: React.FC<{ page: PagesID }> = ({ page }) => {
  const { t, i18n } = useTranslation(["collect-campaign-page"]);
  const { t: inputTranslations } = useTranslation(["input"]);
  const theme = useTheme();
  const { id, idCampaign, idUser } = useParams();
  const { filters } = useFilters();
  const { user } = useAuth();
  const navigate = useNavigate();
  const filterId = `${user?.id_user}_${page}`;
  const [checkBoxData, setCheckBoxData] = useState<any>([]);
  const handleCheckBox = (row: any) => {
    let dataBox = checkBoxData;
    let setData = setCheckBoxData;
    if (dataBox && dataBox.some((el: any) => el.id === row.id)) {
      const removeData = dataBox.filter((el: any) => el.id !== row.id);
      setData(removeData);
    } else {
      setData([...dataBox, row]);
    }
  };
  const { dataDetail: userDetail } = useFetchDetail(
    ServicesURL.customer,
    (idUser || "") + `?corporateId=${id}`,
    PagesID["corporate.collect-campaign.monitor.user.external-training"],
  );

  const { dataDetail: campaign, columns: hdrColumns } = useFetchDataDetail(
    //GET NOTIFICATION DETAILS
    ServicesURL.getCampaign
      .replace(":corporateId", id ? id : "1")
      .replace(":campaignId", idCampaign ?? ""),
    PagesID["corporate.collect-campaign.monitor.user.external-training"],
  );

  const { dataTable: trainingTypeOptions } = useFetchTable(
    `${ServicesURL.getCourseType}`,
    PagesID["corporate.collect-campaign.monitor.user.external-training"],
  );

  const { columns, dataTable, actionForRow, actionsTopToolbar, isLoading } =
    useFetchTable(
      ServicesURL.getCampaignExternalTraining
        .replace(":corporateId", id ? id : "1")
        .replace(":campaignId", idCampaign ?? "")
        .replace(":userId", idUser ?? ""),
      PagesID["corporate.collect-campaign.monitor.user.external-training"],
      filters[filterId],
    );

  const filteredColumns = columns?.filter((col) => {
    return (
      !["points_formatted", "credits_formatted", "hours_formatted"].includes(
        col.accessorKey,
      ) ||
      ((campaign as CollectCampaignDto).unit === "HOURS" &&
        col.accessorKey === "hours_formatted") ||
      ((campaign as CollectCampaignDto).unit === "POINTS" &&
        col.accessorKey === "points_formatted") ||
      ((campaign as CollectCampaignDto).unit === "CREDITS" &&
        col.accessorKey === "credits_formatted")
    );
  });

  const formatNumber = (value?: number): string =>
    Number.isInteger(value) ? (value ?? 0).toString() : (value ?? 0).toFixed(2);

  const tableData = useMemo(() => {
    const target = formatNumber((campaign as CollectCampaignDto).target);
    console.info("datatable", dataTable);
    return dataTable?.content.map((row: any) => {
      const newRow = { ...row };
      if ((campaign as CollectCampaignDto).unit === "HOURS") {
        newRow.hours_formatted = `${formatNumber(row.hours)}/${target}`;
      }
      if ((campaign as CollectCampaignDto).unit === "POINTS") {
        newRow.points_formatted = `${formatNumber(row.points)}/${target}`;
      }
      if ((campaign as CollectCampaignDto).unit === "CREDITS") {
        newRow.credits_formatted = `${formatNumber(row.credits)}/${target}`;
      }
      // if (
      //   row.type === "COURSE" ||
      //   row.type === "PATH" ||
      //   row.type === "ACTIVITY"
      // ) {
      //   newRow.type = inputTranslations(
      //     `options.lo_types.${row.type.toLowerCase()}`,
      //   );
      // } else {
      //   newRow.type = inputTranslations(
      //     `options.activity_type.${row.type.toLowerCase()}`,
      //   );
      // }
      return newRow;
    });
  }, [dataTable, campaign, inputTranslations]);

  const actionsRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => action);
  }, [actionForRow]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = actionsRow && [...actionsRow];

    configRowNew = configRowNew.map((el: ActionsType) => {
      if (el.type === "delete") {
        // return { ...el, disabled: !row.original.deletable };
        return { ...el };
      }

      if (el.type === "edit") {
        // return { ...el, disabled: !row.original.editable };
        return { ...el };
      }
      if (el.key === "download_certificate") {
        return { ...el, disabled: !row.original.certificate_id };
      }
      return { ...el };
    });
    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
        checkBoxData={[checkBoxData]}
        setCheckBoxData={handleCheckBox}
      />,
    ];
  };

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar?.map((action: ActionsType) => {
      switch (action.type) {
        case "button": {
          return {
            ...action,
            labelTranslate: (action.label as any)[i18n.language],
          } as ActionsType;
        }
        default:
          return action;
      }
    });
  }, [actionsTopToolbar, i18n, user?.permissions, checkBoxData, idUser]);

  const customRenderTopToolbar = useCallback(
    ({ table }: { table: any }): React.ReactNode => {
      return hdrColumns ? (
        <CustomTopToolbar
          id={page}
          key={`custom_top_toolbar_${page}`}
          columns={hdrColumns
            .filter((column) => (column as any)["hasFilter"])
            .map((column) => {
              if (column.accessorKey === "type") {
                return { ...column, options: trainingTypeOptions };
              }
              return column;
            })
            .map((column) => ({
              ...column,
              visibility: true,
              visibilityInTable: true,
            }))}
          configurationTopToolbar={actionsToolbar}
          table={table}
          hiddenFilters={false}
          resetFormFilters={undefined}
          setHideHeader={undefined}
          counterElements={false}
          onToolbarChange={() => {}}
          modalTitle={undefined}
          noSummarySelected={undefined}
          filtersException={undefined}
          enableFullScreenToggle={false}
        />
      ) : (
        <></>
      );
    },
    [trainingTypeOptions, columns, actionsToolbar],
  );

  const renderCell = (cel: any, value: any, row: any) => {
    return value !== undefined && value !== null ? value.toString() : "";
  };

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-survey.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t("campaign-user.hero-title", {
            name: userDetail.name,
            surname: userDetail.surname,
          })}
          description={""}
        />
        <ButtonBackPage
          customRedirect={`/corporate/${id}/detail/collect-campaigns/monitor-campaign/${idCampaign}`}
        />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          {isLoading ? (
            <FormLoadingStateCst />
          ) : (
            <Table
              id={page}
              data={tableData || []}
              columns={filteredColumns || []}
              // configurationTopToolbar={actionsToolbar}
              // configurationActionForRow={actionsRow}
              // renderRowActions={customRenderRowActions}
              enableRowActions={false}
              renderTopToolbar={customRenderTopToolbar}
              totalPages={dataTable?.metadata?.total_elements}
              renderCell={renderCell}
            />
          )}
        </Box>
      </Container>
    </AnimationFadeIn>
  );
};
export default MonitoringExternalTraining;
